import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { applyLanguageFilteringSingle } from '@app/common/filtering-behavior';
import { HttpHelper } from '@app/common/http-helper';
import { OrganizationEvent } from '@app/models/organization-events';
import { SimplifiedTerm } from '@app/models/simplified-term';
import { UserChannelTerm } from '@app/models/user-channel-term';
import { CacheService } from '@app/services/cache.service';
import { ChannelService } from '@app/services/channel.service';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { UserChannelTermsService } from '@app/services/user-channel-terms.service';
import { Environment } from '@environments/interface.environment';
import { ListItem } from '@microsoft/microsoft-graph-types';
import { concat, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, map, mergeMap, shareReplay, tap } from 'rxjs/operators';
import { UserLanguageTermService } from './user-language-term.service';

@Injectable({
  providedIn: 'root'
})
export class OrganizationCalendarService {

  constructor(@Inject(CUSTOMERENVIRONMENT) private environment: Environment,
              private http: HttpClient,
              private cache: CacheService,
              private httpHelper: HttpHelper,
              private channelService: ChannelService,
              private userLanguageTermService: UserLanguageTermService,
              private userChannelTermsService: UserChannelTermsService,
              private featureSettingsStoreService: FeatureSettingsStoreService) { }

  private readonly cacheKey = 'OrganizationCalendarService-getOrganizationCalendarEvents';

  public useCache = true;
  private getOrganizationCalendarEventsFromGraph$: Observable<Array<OrganizationEvent>>;
  private getNewOrUpdatedOrganizationCalendarEventsFromGraph$: Observable<Array<OrganizationEvent>>;
  private reloadGetNewOrUpdatedOrganizationCalendarEventsFromGraph$ = new Subject<void>();

  private channels$: Observable<Array<SimplifiedTerm>> = this.channelService.getChannels();
  private userChannelTerms$: Observable<Array<UserChannelTerm>> = this.userChannelTermsService.getUserChannelTerms();
  private calendarSiteUrl = this.featureSettingsStoreService.state.organizationCalenderSiteUrl ? this.featureSettingsStoreService.state.organizationCalenderSiteUrl : this.environment.coreWorkplaceSiteUrl;

  reloadOrganizationCalendarEventsFromGraph() {
    this.reloadGetNewOrUpdatedOrganizationCalendarEventsFromGraph$.next();
    this.getOrganizationCalendarEventsFromGraph$ = null;
  }

  getOrganizationCalendarEventsFromGraph(amount: number = 100): Observable<Array<OrganizationEvent>> {
    if (this.getOrganizationCalendarEventsFromGraph$) {
      return this.getOrganizationCalendarEventsFromGraph$;
    } else {
      const cacheKey = `-${amount.toString()}`;
      const listsRequest = `${this.calendarSiteUrl}/_api/v2.0/sharePoint/getBaseItemByUrl(url='/lists/Events')/items`;
      const now = new Date();
      const httpParams = new HttpParams()
        .append('expand', 'fields(select=Title,EventDate,EndDate,Description,Location,BannerUrl,fRecurrence,fAllDayEvent,RecurrenceData,BoolField_Channel,BoolField_Language)')
        .append('filter', `(fields/EventDate gt '${now.toISOString()}') or (fields/EventDate le '${now.toISOString()}' and fields/EndDate ge '${now.toISOString()}')`)
        .append('top', amount.toString())
        .append('select', 'fields,id,parentReference')
        .append('orderby', 'fields/EventDate asc');
      const getOrganizationCalendarEventsFromGraph$ = forkJoin(
        [
          this.http
            .get<any>(listsRequest, {
              params: httpParams,
              headers: {
                'Prefer': 'HonorNonIndexedQueriesWarningMayFailRandomly'
              }
            }),
          this.userChannelTerms$])
        .pipe(
          map(([listItemResult, userChannelTerms]) => {
            const listItems = listItemResult.value as ListItem[];
            return listItems.map(item => {
              return new OrganizationEvent({
                Title: item.fields['Title'],
                Description: item.fields['Description'],
                Channels: item.fields['BoolField_Channel'],
                ListGuid: item.parentReference['listId'],
                Id: item.id,
                Location: item.fields['Location'],
                EventDate: item.fields['EventDate'],
                EndDate: item.fields['EndDate'],
                BannerUrl: item.fields['BannerUrl'] ? item.fields['BannerUrl'].Url : null,
                AllDayEvent: item.fields['fAllDayEvent'],
                Recurrence: item.fields['fRecurrence'],
                RecurrenceData: item.fields['RecurrenceData'] ? item.fields['RecurrenceData'] : null,
                LanguageTermId: (item.fields['BoolField_Language'] && item.fields['BoolField_Language'].TermGuid) ? item.fields['BoolField_Language'].TermGuid : '',
              });
            }).filter(i => {
              const channelTerms = i.Channels;
              if (channelTerms && channelTerms.length > 0) {
                let found = true;
                const matchingChannels = i.Channels.filter(c => {
                  found = userChannelTerms.some(ct => ct.ChannelTermId === c.TermGuid);
                  if (found) {
                    return c;
                  }
                });
                if (matchingChannels && matchingChannels.length > 0) {
                  return i;
                }
              } else {
                return i;
              }
            });
          }));

      this.getOrganizationCalendarEventsFromGraph$ = getOrganizationCalendarEventsFromGraph$
        .pipe(
          mergeMap((events) => {
            // Language filter toggle
            if (this.featureSettingsStoreService.state.enableLanguageFilteringForSharepointEvents) {
              return this.userLanguageTermService.getUserLanguageTerms().pipe(
                map((userLanguageTerms) => {
                  return applyLanguageFilteringSingle(events, userLanguageTerms, this.featureSettingsStoreService.state.enableShowNonLanguageContentForAllUsers, this.featureSettingsStoreService.state.defaultLanguageTermId);
                })
              );
            } else {
              return of(events);
            }
          }),
          tap(data => {
            this.useCache && this.cache.set(cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getOrganizationCalendarEventsFromGraph', result: new Array<OrganizationEvent>(), rethrow: true, cacheKey: cacheKey })),
          shareReplay({ bufferSize: 1, refCount: true })
        );

      if (this.useCache) {
        const cachedData = this.cache.get<Array<OrganizationEvent>>(this.cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getOrganizationCalendarEventsFromGraph$);
        }
      }
      return this.getOrganizationCalendarEventsFromGraph$;
    }
  }

  getNewOrUpdatedOrganizationCalendarEventsFromGraph(lastCheckedDate: Date, amount: number = 100): Observable<Array<OrganizationEvent>> {
    if (this.getOrganizationCalendarEventsFromGraph$) {
      return this.getOrganizationCalendarEventsFromGraph$;
    } else {
      const listsRequest = `${this.calendarSiteUrl}/_api/v2.0/sharePoint/getBaseItemByUrl(url='/lists/Events')/items`;
      const cacheKey = `-${amount.toString()}`;
      const now = new Date();
      const httpParams = new HttpParams()
        .append('expand', 'fields(select=Title,EventDate,EndDate,Description,Location,BannerUrl,fRecurrence,fAllDayEvent,RecurrenceData,BoolField_Channel,BoolField_Language,Modified)')
        .append('select', 'fields,id,parentReference')
        .append('filter', `(fields/Modified gt '${lastCheckedDate.toISOString()}') and ((fields/EventDate gt '${now.toISOString()}') or (fields/EventDate le '${now.toISOString()}' and fields/EndDate ge '${now.toISOString()}'))`)
        .append('top', amount.toString())
        .append('orderby', 'fields/EventDate desc');

      const getNewOrUpdatedOrganizationCalendarEventsFromGraph$ = forkJoin(
        [
          this.http
            .get<any>(listsRequest, {
              params: httpParams,
              headers: {
                'Prefer': 'HonorNonIndexedQueriesWarningMayFailRandomly'
              }
            }),
          this.userChannelTerms$])
        .pipe(
          map(([listItemResult, userChannelTerms]) => {
            const listItems = listItemResult.value as ListItem[];
            return listItems.map(item => {
              return new OrganizationEvent({
                Title: item.fields['Title'],
                Description: item.fields['Description'],
                Channels: item.fields['BoolField_Channel'],
                ListGuid: item.parentReference['listId'],
                Id: item.id,
                Location: item.fields['Location'],
                EventDate: item.fields['EventDate'],
                EndDate: item.fields['EndDate'],
                BannerUrl: item.fields['BannerUrl'] ? item.fields['BannerUrl'].Url : null,
                AllDayEvent: item.fields['fAllDayEvent'],
                Recurrence: item.fields['fRecurrence'],
                RecurrenceData: item.fields['RecurrenceData'] ? item.fields['RecurrenceData'] : null,
                LanguageTermId: (item.fields['BoolField_Language'] && item.fields['BoolField_Language'].TermGuid) ? item.fields['BoolField_Language'].TermGuid : '',
              });
            }).filter(i => {
              const channelTerms = i.Channels;
              if (channelTerms && channelTerms.length > 0) {
                let found = true;
                const matchingChannels = i.Channels.filter(c => {
                  found = userChannelTerms.some(ct => ct.ChannelTermId === c.TermGuid);
                  if (found) {
                    return c;
                  }
                });
                if (matchingChannels && matchingChannels.length > 0) {
                  return i;
                }
              } else {
                return i;
              }
            });
          }),
          catchError(this.httpHelper.handleError({ operation: 'getNewOrUpdatedOrganizationCalendarEventsFromGraph', result: new Array<OrganizationEvent>(), rethrow: true })));

      this.getNewOrUpdatedOrganizationCalendarEventsFromGraph$ = getNewOrUpdatedOrganizationCalendarEventsFromGraph$
        .pipe(
          mergeMap((events) => {
            // Language filter toggle
            if (this.featureSettingsStoreService.state.enableLanguageFilteringForSharepointEvents) {
              return this.userLanguageTermService
                .getUserLanguageTerms()
                .pipe(
                  map((userLanguageTerms) => {
                    return applyLanguageFilteringSingle(events, userLanguageTerms, this.featureSettingsStoreService.state.enableShowNonLanguageContentForAllUsers, this.featureSettingsStoreService.state.defaultLanguageTermId);
                  })
                );
            } else {
              return of(events);
            }
          }),
          tap(data => {
            this.useCache && this.cache.set(cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getNewOrUpdatedOrganizationCalendarEventsFromGraph', result: new Array<OrganizationEvent>(), rethrow: true, cacheKey: cacheKey }))
        );

      return this.getNewOrUpdatedOrganizationCalendarEventsFromGraph$;
    }
  }
}
